import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { NotificationService } from 'src/@core/services/notification.service';
import { JobService } from 'src/@core/services/job/job.service';
import { AppService } from 'src/@core/services/app.service';
import { forkJoin } from 'rxjs';
import { TasksService } from '../../entity-leads/services/tasks.service';
import {
  TaskActionDialogComponent,
  TaskActionDialogResult,
} from '../../entity-leads/task-action-dialog/task-action-dialog.component';

@Component({
  selector: 'hour4u-admin-edit-tasks-popup',
  templateUrl: './edit-tasks-popup.component.html',
  styleUrls: ['./edit-tasks-popup.component.scss']
})
export class EditTasksPopupComponent implements OnInit {
  tasks: any[] = [];
  closedTasks: any[] = [];
  clientName: string = '';
  isLoading: boolean = false;
  deletingTaskId: string | null = null;
  private hasMutations: boolean = false;
  profile: any;

  constructor(
    private dialogRef: MatDialogRef<EditTasksPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private notify: NotificationService,
    private jobService: JobService,
    private tasksService: TasksService,
    private appService: AppService
  ) { }

  ngOnInit() {
    this.profile = this.appService.getCurrentAccount();
    if (this.data && this.data.open_tasks && this.data.open_tasks.tasks) {
      // Create a copy of tasks to avoid mutating the original
      this.tasks = this.data.open_tasks.tasks
        .filter((task: any) => !this.isClosedTask(task))
        .map((task: any) => {
          // Set isCompleted based on executed field
          const isExecuted = task.executed === true;
          return {
            ...task,
            isCompleted: isExecuted, // Set to true if executed is true
            completion_reason: task.completion_reason || '' // Use existing reason or empty string
          };
        });
    }
    this.closedTasks = this.buildClosedTasksList();
    this.clientName = (this.data && this.data.client_name) ? this.data.client_name : '';
  }

  private isClosedTask(task: any): boolean {
    if (!task) {
      return false;
    }
    if (task.executed === true) {
      return true;
    }
    const status = String(task.status || '').toLowerCase();
    return status === 'executed' || status === 'completed' || status === 'cancelled';
  }

  private buildClosedTasksList(): any[] {
    const closedFromPayload =
      (this.data && this.data.closed_tasks && this.data.closed_tasks.tasks) || [];
    const legacyClosed =
      (this.data && this.data.open_tasks && this.data.open_tasks.tasks
        ? this.data.open_tasks.tasks.filter((task: any) => this.isClosedTask(task))
        : []) || [];
    const seen = new Set<string>();
    const merged: any[] = [];
    [...closedFromPayload, ...legacyClosed].forEach((task: any) => {
      const key = String(task && (task.task_id || task.follow_up_schedule_name || task.task_description) || '');
      if (key && seen.has(key)) {
        return;
      }
      if (key) {
        seen.add(key);
      }
      merged.push({ ...task });
    });
    return merged;
  }

  closeDialog() {
    this.dialogRef.close(this.hasMutations ? true : undefined);
  }

  deleteOpenTask(task: any, event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!task || !task.task_id || this.isLoading || this.deletingTaskId) {
      if (task && !task.task_id) {
        this.notify.showMessage('This task is missing a task id and cannot be deleted', 2500);
      }
      return;
    }

    const dialogRef = this.dialog.open(TaskActionDialogComponent, {
      width: '520px',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: true,
      data: {
        mode: 'cancel',
        taskTitle: task.task_description || task.title,
        leadName: this.clientName,
        scheduledAt: task.task_datetime || task.scheduled_at,
      },
    });

    dialogRef.afterClosed().subscribe((result: TaskActionDialogResult) => {
      if (!result || !result.confirmed) {
        return;
      }
      this.deletingTaskId = task.task_id;
      const entityId =
        (this.profile && this.profile.entity && this.profile.entity.id)
          ? this.profile.entity.id
          : '';
      const jobId = task.job_id || (this.data && this.data.job_id) || '';

      this.tasksService.cancelTask(task.task_id, {
        reason: result.reason || 'Deleted from Edit tasks',
        job_id: jobId || undefined,
        entity_id: entityId || undefined,
      }).subscribe(
        () => {
          this.deletingTaskId = null;
          this.hasMutations = true;
          this.tasks = this.tasks.filter((t) => t.task_id !== task.task_id);
          this.closedTasks = [
            {
              ...task,
              status: 'cancelled',
              executed: false,
              isCompleted: false,
              completion_reason: result.reason || 'Deleted from Edit tasks',
            },
            ...this.closedTasks,
          ];
          this.notify.showMessage('Task deleted', 2000);
        },
        (error) => {
          this.deletingTaskId = null;
          let errorMessage = 'Error deleting task';
          if (error && error.error) {
            if (error.error.error) {
              errorMessage = error.error.error;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            }
          } else if (error && error.message) {
            errorMessage = error.message;
          }
          this.notify.showMessage(errorMessage, 2500);
          console.error('Error deleting task:', error);
        }
      );
    });
  }

  updateTasks() {
    // Filter out agent tasks - they should not be manually completed
    const completedTasks = this.tasks.filter(task => task.isCompleted && !this.isTaskAssignedToAgent(task));

    if (completedTasks.length === 0) {
      this.notify.showMessage('Please select at least one task to mark as completed', 2000);
      return;
    }

    // Validate that all completed tasks have a completion reason
    const tasksWithoutReason = completedTasks.filter(task => !task.completion_reason || task.completion_reason.trim() === '');
    if (tasksWithoutReason.length > 0) {
      this.notify.showMessage('Please provide a reason for completion for all selected tasks', 2000);
      return;
    }

    this.isLoading = true;

    const entity_id = (this.profile && this.profile.entity && this.profile.entity.id) ? this.profile.entity.id : '';

    if (!entity_id) {
      this.isLoading = false;
      this.notify.showMessage('Missing required information', 2000);
      return;
    }

    // Validate that all completed tasks have job_id and task_id
    const tasksWithoutIds = completedTasks.filter(task => !task.job_id || !task.task_id);
    if (tasksWithoutIds.length > 0) {
      this.isLoading = false;
      this.notify.showMessage('Some tasks are missing required information (job_id or task_id)', 2000);
      return;
    }

    // Create API calls for all completed tasks
    const updateCalls = completedTasks.map(task => {
      return this.jobService.updateTaskCompletion(
        task.job_id,
        task.task_id,
        task.completion_reason.trim(),
        entity_id
      );
    });

    // Execute all API calls in parallel
    forkJoin(updateCalls).subscribe(
      (responses) => {
        this.isLoading = false;
        this.hasMutations = true;
        this.notify.showMessage('Tasks updated successfully!', 2000);
        this.dialogRef.close(true);
      },
      (error) => {
        this.isLoading = false;
        // Extract error message from API response: {"error": "message"}
        let errorMessage = 'Error updating tasks';
        if (error && error.error) {
          if (error.error.error) {
            errorMessage = error.error.error;
          } else if (error.error.message) {
            errorMessage = error.error.message;
          }
        } else if (error && error.message) {
          errorMessage = error.message;
        }
        this.notify.showMessage(errorMessage, 2000);
        console.error('Error updating tasks:', error);
      }
    );
  }

  getTaskIcon(task: any): string {
    return task.task_type === 'schedule_call' ? 'star' : 'person';
  }

  isTaskAssignedToAgent(task: any): boolean {
    return task && task.task_assigned_to === 'agent';
  }

  isTaskAssignedToHuman(task: any): boolean {
    return task && task.task_assigned_to && task.task_assigned_to !== 'agent';
  }

  hasCompletedTasks(): boolean {
    return this.tasks.some(task => task.isCompleted);
  }

  private parseTaskDate(task: any): Date | null {
    if (!task || task.task_datetime == null || task.task_datetime === '') {
      return null;
    }

    const raw = task.task_datetime;
    let date: Date;

    if (typeof raw === 'number') {
      date = new Date(raw < 1e12 ? raw * 1000 : raw);
    } else if (typeof raw === 'string' && /^\d+$/.test(raw.trim())) {
      const num = Number(raw);
      date = new Date(num < 1e12 ? num * 1000 : num);
    } else {
      date = new Date(raw);
    }

    return isNaN(date.getTime()) ? null : date;
  }

  formatTaskDatetime(task: any): string | null {
    const date = this.parseTaskDate(task);
    if (!date) {
      return null;
    }

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const hours = date.getHours();
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : `${date.getMinutes()}`;

    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} · ${formattedHours}:${formattedMinutes} ${period}`;
  }

  // Human-friendly "when" relative to now, e.g. "Due in 3h" / "Overdue by 2d".
  getTaskDueLabel(task: any): string | null {
    const date = this.parseTaskDate(task);
    if (!date || task.executed === true) {
      return null;
    }

    const diffMs = date.getTime() - Date.now();
    const overdue = diffMs < 0;
    const minutes = Math.floor(Math.abs(diffMs) / 60000);

    let amount: string;
    if (minutes < 1) {
      return overdue ? 'Just now' : 'Due now';
    } else if (minutes < 60) {
      amount = `${minutes}m`;
    } else if (minutes < 1440) {
      amount = `${Math.floor(minutes / 60)}h`;
    } else {
      amount = `${Math.floor(minutes / 1440)}d`;
    }

    return overdue ? `Overdue by ${amount}` : `Due in ${amount}`;
  }

  isTaskOverdue(task: any): boolean {
    const date = this.parseTaskDate(task);
    return !!date && task.executed !== true && date.getTime() < Date.now();
  }

  getStatusLabel(status: string): string {
    return status ? status.replace(/_/g, ' ') : '';
  }

  getStatusClass(status: string): string {
    const s = (status || '').toLowerCase();
    if (s === 'running') return 'task-status task-status--running';
    if (s === 'awaiting_approval') return 'task-status task-status--approval';
    if (s === 'failed') return 'task-status task-status--failed';
    if (s === 'completed') return 'task-status task-status--completed';
    if (s === 'scheduled') return 'task-status task-status--scheduled';
    if (s === 'paused') return 'task-status task-status--paused';
    if (s === 'cancelled') return 'task-status task-status--completed';
    return 'task-status';
  }
}
